import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import SearchableSelect from './SearchableSelect';
import { fetchAllProvidersBasicInfo } from '../services/api';

/*
OnSelect contract:
when an option is selected the onSelect callback
gets called with the provider object as its only arg
provider object:
{
  email,
  id,
  firstName,
  lastName,
  fullTitle,
}
------------------------
onChange contract
optional callback for when user types into search bar, takes user's input as only arg
------------------------
customFilter contract:
optional sorting mechanism. sorts provider objects of form
{
  label: "firstName lastName (MM/DD/YYYY)",
  value: {
    email,
    id,
    firstName,
    lastName,
    fullTitle,
  }
}
*/

function ProviderSearchBar(props) {
  const {
    disabled,
    onSelect,
    errorMessage,
    allowSendToQwell,
    customFilter = null,
    clearable=true,
    value,
    label = "For Provider",
    name = "provider",
    noOptionsMessage="Start typing for options",
    placeholder = "Search for a provider...",
    maxOptions = 20,
    searchable = true,
    practiceId = null,
    from = null,
    classes: {
      selectInput,
      customWidth
    }
  } = props;

  const [providers, setProviders] = useState([]);
  const [currentProviderId, setCurrentProviderId] = useState(value);

  // loading provider names
  useEffect(() => {
    (async () => {
      const allProvidersBasicInfo = await fetchAllProvidersBasicInfo(practiceId);
      const providerList = allProvidersBasicInfo.map(p => ({
        label: p.fullTitle,
        value: p.id,
        info: p,
      }))
      if (allowSendToQwell) providerList.push({
        label: 'Send to Qwell',
        value: 'send-to-qwell',
        info: { id: 'send-to-qwell' },
      });
      setProviders(providerList);
    })();
  }, [])

  // filtering provider names provider names appropriately when search bar is typed into
  const filterProviders = async providerName => {
    return providers.filter(customFilter || ((provider) =>
      provider.label.toLowerCase().includes(providerName.toLowerCase())
    )).slice(0, maxOptions);
  };

  const handleSelectionBeingMade = async (provider) => {
    if (provider) {
      setCurrentProviderId(provider.id)
      await onSelect(provider.info)
    } else {
      setCurrentProviderId(null)
      await onSelect(null)
    }
  }

  const getList = async(inputValue) =>{
      const allProvidersBasicInfo = await fetchAllProvidersBasicInfo(practiceId);
      const providerList =  allProvidersBasicInfo.map(p => ({
        label: p.fullTitle,
        value: p.id,
        info: p,
      }))
      setProviders(providerList);
      return providerList
      .filter(
        customFilter ||
        ((provider, i) =>
          provider.label.toLowerCase().includes(inputValue.toLowerCase()))
      )
      .slice(0, maxOptions);
    }

  return (
    <SearchableSelect
      noOptionsMessage={noOptionsMessage}
      clearable={clearable}
      disabled={disabled}
      searchable={searchable}
      fetchAsyncOptions={filterProviders}
      placeholder={placeholder}
      label={label}
      name={name}
      onChange={handleSelectionBeingMade}
      value={currentProviderId}
      options={[]}
      className={(from) ? customWidth:selectInput}
      error={!!errorMessage}
      errorMessages={errorMessage}
      getList={getList}
    />
  );
}

ProviderSearchBar.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  customFilter: PropTypes.func,
  placeholder: PropTypes.string,
  practiceId: PropTypes.string,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  value: PropTypes.string,
  name: PropTypes.string,
  maxOptions: PropTypes.number,
  searchable: PropTypes.bool,
  disabled: PropTypes.bool,
}

const styles = () => ({
  selectInput: {
    marginTop: '0.5em',
    marginBottom: '1em',
  },
  customWidth: {
    width: '50%',
    marginTop: '0.5em',
    marginBottom: '1em',
  }
});

export default withStyles(styles)(ProviderSearchBar);
